// 角色分頁（SPEC #vms-web-772；VMS UX 評審 2026-10-13 P0-9、B-26）：一張表分段列出所有會出現在畫面上的角色——
// 組織層（擁有者、組織管理員、成員 / 訪客、稽核員）、場域層（節點管理員、操作員、觀看者、安裝技師）、家戶層，每個角色一行「可以做」一行「不可以」；
// 自訂角色範本（IAM-03）接在後面。
// 自訂角色拿掉動作 → 用這個角色的授權立刻縮權；加動作 → 不自動擴權（伺服器回待套用數），按〔套用〕才加到既有授權（IAM-04）。
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isOrgAdmin, orgApi, type MyOrg, type RoleTemplate } from '../../lib/planeApi';
import { Dialog } from '../../components/Dialog';
import { errText } from '../settings/OrgSection';

export function roleName(t: (k: string, o?: Record<string, unknown>) => string, r: Pick<RoleTemplate, 'role_id' | 'name' | 'system'>): string {
  return r.system ? t(`roles.${r.role_id}`, { defaultValue: r.name }) : r.name;
}

/** 角色表的固定列（SPEC #vms-web-772）：層、key（iam.rl_<key>_name / _can / _cannot / _how）。 */
export const ROLE_TABLE: { layer: 'org' | 'site' | 'home'; key: string }[] = [
  { layer: 'org', key: 'owner' },
  { layer: 'org', key: 'org_admin' },
  { layer: 'org', key: 'member' },
  { layer: 'org', key: 'auditor' },
  { layer: 'site', key: 'node_admin' },
  { layer: 'site', key: 'operator' },
  { layer: 'site', key: 'viewer' },
  { layer: 'site', key: 'installer' },
  { layer: 'site', key: 'maintainer' },
  { layer: 'home', key: 'home_roles' },
];

export function RolesTab({ org }: { org: MyOrg }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const roles = useQuery({ queryKey: ['org-roles', org.org_id], queryFn: () => orgApi.roles(org) });
  const [editing, setEditing] = useState<RoleTemplate | 'new' | null>(null);
  const [applying, setApplying] = useState<RoleTemplate | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const admin = isOrgAdmin(org);
  const custom = (roles.data?.roles ?? []).filter((r) => !r.system);
  const words = (as: string[]) => as.map((a) => t(`actions.${a}`, { defaultValue: a })).join(t('common.list_sep'));
  return (
    <section className="card">
      <div className="toolbar">
        <h2 className="section-title">{t('people.roles')}</h2>
        <div className="spacer" />
        {admin && (
          <button className="btn btn-primary btn-sm" onClick={() => setEditing('new')}>
            {t('people.new_role')}
          </button>
        )}
      </div>
      <p className="muted small">{t('iam.roles_hint')}</p>
      {notice && <p className="notice">{notice}</p>}
      <div className="table-wrap">
        <table className="grid ot-cards" data-testid="role-table">
          <thead>
            <tr>
              <th>{t('iam.col_layer')}</th>
              <th>{t('people.role')}</th>
              <th>{t('iam.col_can')}</th>
              <th>{t('iam.col_cannot')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {ROLE_TABLE.map((r, i) => (
              <tr key={r.key} data-role={r.key}>
                <td data-label={t('iam.col_layer')} className="small muted">
                  {i === 0 || ROLE_TABLE[i - 1].layer !== r.layer ? t('iam.layer_' + r.layer) : ''}
                </td>
                <td data-label={t('people.role')}>
                  <div>{t(`iam.rl_${r.key}_name`)}</div>
                  <div className="muted small">{t(`iam.rl_${r.key}_how`)}</div>
                </td>
                <td data-label={t('iam.col_can')} className="small wrap">
                  {t(`iam.rl_${r.key}_can`)}
                </td>
                <td data-label={t('iam.col_cannot')} className="small wrap">
                  {t(`iam.rl_${r.key}_cannot`)}
                </td>
                <td />
              </tr>
            ))}
            {custom.map((r, i) => (
              <tr key={r.role_id} data-role={r.role_id}>
                <td data-label={t('iam.col_layer')} className="small muted">
                  {i === 0 ? t('iam.layer_custom') : ''}
                </td>
                <td data-label={t('people.role')}>
                  <div>{roleName(t, r)}</div>
                  {r.description && <div className="muted small">{r.description}</div>}
                </td>
                <td data-label={t('iam.col_can')} className="small wrap">
                  {words(r.actions)}
                </td>
                <td data-label={t('iam.col_cannot')} className="small muted">
                  {t('iam.custom_cannot')}
                </td>
                <td>
                  <div className="row">
                    {!!r.pending_apply && (
                      <span className="badge badge-warn" data-testid="role-pending">
                        {t('people.pending_chip', { n: r.pending_apply })}
                      </span>
                    )}
                    {admin && !!r.pending_apply && (
                      <button className="btn btn-sm btn-primary" onClick={() => setApplying(r)}>
                        {t('people.apply')}
                      </button>
                    )}
                    {admin && (
                      <button className="btn btn-sm" onClick={() => setEditing(r)}>
                        {t('common.edit')}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="muted small">{t('iam.roles_export_note')}</p>
      <ApplyDialog
        org={org}
        role={applying}
        close={() => setApplying(null)}
        done={(n) => {
          setNotice(t('people.applied', { n }));
          void qc.invalidateQueries({ queryKey: ['org-roles', org.org_id] });
          void qc.invalidateQueries({ queryKey: ['org-access', org.org_id] });
        }}
      />
      <RoleDialog
        org={org}
        role={editing}
        allActions={roles.data?.actions ?? []}
        close={() => setEditing(null)}
        done={(pending) => {
          setNotice(pending ? t('people.pending_apply', { n: pending }) : null);
          void qc.invalidateQueries({ queryKey: ['org-roles', org.org_id] });
          void qc.invalidateQueries({ queryKey: ['org-access', org.org_id] });
        }}
      />
    </section>
  );
}

function RoleDialog({ org, role, allActions, close, done }: { org: MyOrg; role: RoleTemplate | 'new' | null; allActions: string[]; close(): void; done(pending: number): void }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [actions, setActions] = useState<string[]>([]);
  const [key, setKey] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const k = role === 'new' ? 'new' : (role?.role_id ?? '');
  if (k !== key) {
    setKey(k);
    setErr(null);
    setName(role && role !== 'new' ? role.name : '');
    setDesc(role && role !== 'new' ? (role.description ?? '') : '');
    setActions(role && role !== 'new' ? role.actions : ['device.read', 'media.live']);
  }
  const save = useMutation({
    mutationFn: async () => {
      if (role === 'new') {
        await orgApi.createRole(org, { name: name.trim(), description: desc.trim() || undefined, actions });
        return 0;
      }
      const r = await orgApi.updateRole(org, role!.role_id, { expected_version: role!.version, name: name.trim(), actions });
      return r.pending_apply;
    },
    onSuccess: (pending) => {
      done(pending);
      close();
    },
    onError: (e) => setErr(errText(t, e)),
  });
  function submit(e: FormEvent) {
    e.preventDefault();
    save.mutate();
  }
  return (
    <Dialog open={!!role} title={role === 'new' ? t('people.new_role') : t('people.edit_role')} onClose={close}>
      <form className="form" onSubmit={submit}>
        <label className="field">
          <span>{t('people.role_name')}</span>
          <input className="input" required maxLength={40} value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        {role === 'new' && (
          <label className="field">
            <span>{t('people.role_desc')}</span>
            <input className="input" maxLength={200} value={desc} onChange={(e) => setDesc(e.target.value)} />
          </label>
        )}
        <fieldset className="field">
          <legend>{t('people.actions')}</legend>
          <div className="check-grid">
            {allActions.map((a) => (
              <label key={a} className="check">
                <input type="checkbox" checked={actions.includes(a)} onChange={(e) => setActions(e.target.checked ? [...actions, a] : actions.filter((x) => x !== a))} />
                <span>{t(`actions.${a}`, { defaultValue: a })}</span>
              </label>
            ))}
          </div>
        </fieldset>
        {role !== 'new' && <p className="small muted">{t('people.role_edit_note')}</p>}
        {err && <div className="error">{err}</div>}
        <div className="row-end">
          <button type="button" className="btn" onClick={close}>
            {t('common.cancel')}
          </button>
          <button className="btn btn-primary" disabled={save.isPending || actions.length === 0}>
            {t('common.save')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

function ApplyDialog({ org, role, close, done }: { org: MyOrg; role: RoleTemplate | null; close(): void; done(applied: number): void }) {
  const { t } = useTranslation();
  const [err, setErr] = useState<string | null>(null);
  const apply = useMutation({
    mutationFn: () => orgApi.applyRole(org, role!.role_id, role!.version),
    onSuccess: (r) => {
      done(r.applied);
      close();
    },
    onError: (e) => setErr(errText(t, e)),
  });
  return (
    <Dialog
      open={!!role}
      title={t('people.apply_title')}
      onClose={() => {
        setErr(null);
        close();
      }}
    >
      {role && (
        <div className="form">
          <p>{t('people.apply_confirm', { role: role.name, n: role.pending_apply ?? 0 })}</p>
          <p className="small muted">{role.actions.map((a) => t(`actions.${a}`, { defaultValue: a })).join(t('common.list_sep'))}</p>
          {err && <div className="error">{err}</div>}
          <div className="row-end">
            <button type="button" className="btn" onClick={close}>
              {t('common.cancel')}
            </button>
            <button className="btn btn-primary" disabled={apply.isPending} onClick={() => apply.mutate()}>
              {t('people.apply')}
            </button>
          </div>
        </div>
      )}
    </Dialog>
  );
}
